/* eslint-disable react/forbid-prop-types */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { LinearProgress, Button, Typography, InputAdornment, Paper, IconButton, Box } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import SearchIcon from '@mui/icons-material/Search';

import SimpleNoFilterReactTable from 'components/table/SimpleNoFilterReactTable';
import ServiceCategoryService from 'services/ServiceCategoryService';

import showNotification from 'commons/helpers/showNotification';
import DebouncedTextInput from 'components/inputs/DebouncedTextInput';

import ServiceCategoryFormModal from './ServiceCategoryFormModal';
import { FontAwesomeIconStatus } from 'components/icon/FontAwesomeIconStatus';

const useStyles = makeStyles()((theme) => ({
  root: {
    padding: theme.spacing(3),
  },
  toolbar: {
    height: '42px',
  },
  content: {
    marginTop: theme.spacing(2),
  },
  filterBar: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  inputSearchName: { minWidth: 350, marginLeft: '10px' },
  iconSearch: { color: '#b3b3b3' },
  outlinedInput: {
    padding: '11px 14px !important',
  },
  row: {
    display: 'flex',
    alignItems: 'center',
  },
  spacer: {
    flexGrow: 1,
  },
  btnCategories: {
    marginRight: '2rem',
  },
}));

const COLUMNS = [
  {
    Header: 'Nome da categoria',
    accessor: 'name',
  },
];

// COMPONENT
function ServiceCategoryHomePage() {
  const refSelectedObject = React.useRef();
  const { classes } = useStyles();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [dataSource, setDataSource] = useState([]);
  const [nameSearchTerm, setNameSearchTerm] = useState('');

  const reloadDataSource = React.useCallback(() => {
    ServiceCategoryService.findAllForCurrentUser()
      .then((resp) => {
        setDataSource(resp);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, []);

  useEffect(() => {
    reloadDataSource();
  }, [reloadDataSource]);

  const columns = React.useMemo(() => COLUMNS, []);
  const initialState = React.useMemo(() => ({ pageSize: 25, sortBy: [{ id: 'name', desc: false }] }), []);

  // computed
  const filteredData = React.useMemo(() => {
    return dataSource.filter((data) => {
      return (
        nameSearchTerm.trim().length === 0 || data.name.toLowerCase().includes(nameSearchTerm.toLowerCase())
      );
    });
  }, [dataSource, nameSearchTerm]);

  return (
    <div className={classes.root}>
      <ServiceCategoryFormModal
        serviceCategoryId={refSelectedObject.current?.id}
        open={formModalOpen}
        onClose={() => {
          setFormModalOpen(false);
        }}
        onCreateCallback={() => {
          reloadDataSource();
          setFormModalOpen(false);
        }}
        onUpdateCallback={() => {
          reloadDataSource();
          setFormModalOpen(false);
        }}
      />
      {/* TOOLBAR */}
      <div className={classes.toolbar}>
        <div className={classes.row}>
          <Box display="flex" direction="row" justify="center" alignItems="center">
            <IconButton onClick={() => navigate(-1)} style={{ marginRight: 4 }} size="large">
              <FontAwesomeIconStatus icon="arrow-left" status="primary" />
            </IconButton>
            <Typography variant="overline">Categorias de Serviço</Typography>
          </Box>
          <span className={classes.spacer} />

          <Button
            color="primary"
            variant="contained"
            onClick={() => {
              refSelectedObject.current = null;
              setFormModalOpen(true);
            }}
          >
            Adicionar Categoria
          </Button>
        </div>
        <div style={{ marginTop: '5px' }}>
          {isLoading ? <LinearProgress style={{ width: '100%' }} /> : <div style={{ height: '4px' }} />}
        </div>
      </div>

      {/* TABLE */}
      <Paper className={classes.content} elevation={1}>
        <div className={classes.filterBar}>
          <DebouncedTextInput
            onChange={setNameSearchTerm}
            placeholder="Pesquisar por nome..."
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon className={classes.iconSearch} />
                </InputAdornment>
              ),
              classes: { input: classes.outlinedInput },
            }}
            className={classes.inputSearchName}
            variant="outlined"
          />
        </div>
        <SimpleNoFilterReactTable
          dataSource={filteredData}
          columns={columns}
          initialState={initialState}
          onSelectCallback={(object) => {
            refSelectedObject.current = object.original;
            setFormModalOpen(true);
          }}
          onEditCallback={(productCategoryEdited) => {
            refSelectedObject.current = productCategoryEdited;
            setFormModalOpen(true);
          }}
          onDeleteCallback={(objectDeleted) => {
            setIsLoading(true);
            ServiceCategoryService.delete(objectDeleted)
              .then(() => {
                showNotification('success', 'Deletado com sucesso.');
                setDataSource((prevDataSource) =>
                  prevDataSource.filter((obj) => obj.id !== objectDeleted.id),
                );
              })
              .finally(() => {
                setIsLoading(false);
              });
          }}
          actionsDisabled={isLoading}
          size="small"
        />
      </Paper>
    </div>
  );
}

export default ServiceCategoryHomePage;
